import styled from '@emotion/styled';
import { FC } from 'react';

export type TPlayerIndexes = {
  amazement: number;
  attention: number;
  happiness: number;
  involvement: number;
  tiredness: number;
};

export const DEFAULT_PLAYER_INDEXES: TPlayerIndexes = {
  amazement: 0,
  attention: 0,
  happiness: 0,
  involvement: 0,
  tiredness: 0,
};

export const normalizeIndexValue = (value: unknown) =>
  Math.round(Number(value || 0));

const Wrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: clamp(8px, 1.3vw, 18px);
  width: 100%;
  min-height: 48px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.COLORS.ACCENT._800}66;
  border-radius: 8px;
  background: ${({ theme }) => theme.COLORS.BLACK._400}c9;
  box-shadow: 0 0 18px ${({ theme }) => theme.COLORS.ACCENT._200}16;
  backdrop-filter: blur(10px);

  @media (max-width: 720px), (pointer: coarse) {
    gap: 6px;
    min-height: 42px;
    overflow-x: auto;
    padding: 6px 8px;
    border-radius: 12px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const MetricsGroup = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: clamp(8px, 1.3vw, 18px);
  flex: 0 0 auto;

  @media (max-width: 720px), (pointer: coarse) {
    gap: 6px;
  }
`;

const Title = styled('div')`
  overflow: hidden;
  flex: 1 1 180px;
  min-width: 140px;
  margin-left: auto;
  color: ${({ theme }) => theme.COLORS.WHITE._100};
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 720px), (pointer: coarse) {
    min-width: 120px;
    font-size: 10px;
  }
`;

const Metric = styled('div')`
  display: flex;
  min-width: 82px;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  @media (max-width: 720px), (pointer: coarse) {
    min-width: 46px;
    gap: 2px;
  }
`;

const MetricValue = styled('div')`
  min-width: 56px;
  padding: 3px 10px;
  border-radius: 999px;
  color: ${({ theme }) => theme.COLORS.ACCENT._100};
  background: rgb(0 0 0 / 0.34);
  font-size: 15px;
  font-weight: 800;
  line-height: 1.1;
  text-align: center;

  @media (max-width: 720px), (pointer: coarse) {
    min-width: 38px;
    padding: 2px 5px;
    font-size: 8px;
  }
`;

const MetricLabel = styled('div')`
  color: ${({ theme }) => theme.COLORS.WHITE._300};
  font-size: 11px;
  font-weight: 600;
  line-height: 1.1;
  text-align: center;
  white-space: nowrap;

  @media (max-width: 720px), (pointer: coarse) {
    max-width: 46px;
    overflow: hidden;
    font-size: 5px;
    line-height: 7px;
    text-overflow: ellipsis;
  }
`;

type TProps = {
  indexes?: TPlayerIndexes | null;
  title: string;
};

export const VideoPlayerIndexesBar: FC<TProps> = ({ indexes, title }) => {
  const values = indexes ?? DEFAULT_PLAYER_INDEXES;

  return (
    <Wrapper aria-label="Viewing analytics indexes">
      <MetricsGroup>
        <Metric>
          <MetricValue>{values.attention}%</MetricValue>
          <MetricLabel>Attentiveness</MetricLabel>
        </Metric>
        <Metric>
          <MetricValue>{values.involvement}%</MetricValue>
          <MetricLabel>Involvement</MetricLabel>
        </Metric>
        <Metric>
          <MetricValue>{values.happiness}%</MetricValue>
          <MetricLabel>Emotionality</MetricLabel>
        </Metric>
        <Metric>
          <MetricValue>{values.amazement}%</MetricValue>
          <MetricLabel>Distraction</MetricLabel>
        </Metric>
        <Metric>
          <MetricValue>{values.tiredness}%</MetricValue>
          <MetricLabel>Tiredness</MetricLabel>
        </Metric>
      </MetricsGroup>
      <Title>{title}</Title>
    </Wrapper>
  );
};
